import { propertyHooks } from '$src/lib/trpcHooks/propertyHooks'
import { useLingui } from '@lingui/react/macro'
import { visitHooks } from '$src/lib/trpcHooks'
import { useAuthStore } from '$src/store/useAuthStore'
import { getAcquisitionDataFromStorage } from '$src/utils/utm'
import { Box, Stepper } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { AppModal } from '@markku/components'
import { AvailableTimeSlot } from '@markku/types'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { CityStep } from './steps/CityStep'
import { DateTimeStep } from './steps/DateTimeStep'
import { InfoFormValues, InfoStep } from './steps/InfoStep'
import { SuccessStep } from './steps/SuccessStep'

interface BookSurveyVisitModalProps {
  opened: boolean
  onClose: () => void
}

function getInitialBookingDate(): Date {
  const now = dayjs()
  const currentHour = now.hour()

  // If it's before 3 PM on a weekday, show today
  if (currentHour < 15 && now.day() >= 1 && now.day() <= 5) {
    return now.toDate()
  }

  // Otherwise, get next weekday
  const nextDay = now.add(1, 'day')
  const dayOfWeek = nextDay.day()

  if (dayOfWeek === 6) {
    return nextDay.add(2, 'day').toDate()
  }
  if (dayOfWeek === 0) {
    return nextDay.add(1, 'day').toDate()
  }

  return nextDay.toDate()
}

export function BookSurveyVisitModal({
  opened,
  onClose,
}: BookSurveyVisitModalProps) {
  const { t } = useLingui()
  const isMobile = useMediaQuery('(max-width: 768px)', true, {
    getInitialValueInEffect: false,
  })
  const user = useAuthStore((state) => state.user)

  const [activeStep, setActiveStep] = useState(0)
  const [city, setCity] = useState('')
  const [selectedDate, setSelectedDate] = useState(getInitialBookingDate())
  const [selectedTime, setSelectedTime] = useState<AvailableTimeSlot>()
  const [bookedVisit, setBookedVisit] = useState<{
    startTime: string
    endTime: string
  }>()

  const { data: propertiesResponse } = propertyHooks.useList()
  const properties = propertiesResponse?.data || []

  const { data: allTimeSlots, isLoading: isLoadingSlots } =
    visitHooks.useGetAvailableTimeSlots()

  const { mutateAsync: bookVisit, isPending: isSubmitting } =
    visitHooks.useBookSurveyVisit()

  // Auto-select first available date when slots load
  useEffect(() => {
    if (allTimeSlots?.length && activeStep === 1) {
      const firstSlotDate = dayjs(allTimeSlots[0].startTime)
        .startOf('day')
        .toDate()
      setSelectedDate(firstSlotDate)
    }
  }, [allTimeSlots, activeStep])

  // Filter time slots for the selected date
  const filteredTimeSlots = useMemo(() => {
    if (!allTimeSlots) return []

    const selectedDayStart = dayjs(selectedDate).startOf('day')
    const selectedDayEnd = dayjs(selectedDate).endOf('day')

    return allTimeSlots.filter((slot) => {
      const slotTime = dayjs(slot.startTime)
      return (
        slotTime.isAfter(selectedDayStart) && slotTime.isBefore(selectedDayEnd)
      )
    })
  }, [selectedDate, allTimeSlots])

  const handleInfoSubmit = async (values: InfoFormValues) => {
    if (!selectedTime || !user) return

    try {
      const result = await bookVisit({
        city,
        startTime: selectedTime.startTime,
        userId: selectedTime.userId,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        propertyId: values.propertyId,
        street: values.street,
        postalCode: values.postalCode,
        propertyType: values.propertyType,
        notes: values.notes,
        acquisitionData: getAcquisitionDataFromStorage(),
      })

      setBookedVisit({
        startTime: result.startTime,
        endTime: result.endTime,
      })
      setActiveStep(3) // Go to success step
    } catch (error) {
      console.error('Failed to book visit:', error)
    }
  }

  const handleClose = () => {
    setActiveStep(0)
    setCity('')
    setSelectedDate(getInitialBookingDate())
    setSelectedTime(undefined)
    setBookedVisit(undefined)
    onClose()
  }

  if (!user) return null

  return (
    <AppModal
      opened={opened}
      onClose={handleClose}
      title={t`Book a free survey visit`}
      size="lg"
      fullScreenOnMobile
      showFooter={false}
    >
      <Stepper active={activeStep} mt="md" mb="xl" size="sm" wrap={false}>
        <Stepper.Step label={t`City`} />
        <Stepper.Step label={t`Time`} />
        <Stepper.Step label={t`Details`} />
      </Stepper>

      <Box style={{ minHeight: 400 }} pb={isMobile ? 'xl' : undefined}>
        {activeStep === 0 && (
          <CityStep
            value={city}
            onChange={setCity}
            onNext={() => setActiveStep(1)}
          />
        )}

        {activeStep === 1 && (
          <DateTimeStep
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onDateChange={setSelectedDate}
            onTimeSelect={setSelectedTime}
            timeSlots={filteredTimeSlots}
            isLoading={isLoadingSlots}
            onNext={() => setActiveStep(2)}
            onBack={() => setActiveStep(0)}
            isSubmitting={false}
          />
        )}

        {activeStep === 2 && selectedTime && (
          <InfoStep
            initialFirstName={user.firstName}
            initialLastName={user.lastName}
            initialPhoneNumber={user.phoneNumber || undefined}
            properties={properties}
            city={city}
            selectedDate={selectedDate}
            selectedTime={new Date(selectedTime.startTime)}
            selectedEndTime={new Date(selectedTime.endTime)}
            onSubmit={handleInfoSubmit}
            onBack={() => setActiveStep(1)}
            isSubmitting={isSubmitting}
          />
        )}

        {activeStep === 3 && bookedVisit && (
          <SuccessStep
            selectedDate={selectedDate}
            selectedTime={new Date(bookedVisit.startTime)}
            estimatedEndTime={new Date(bookedVisit.endTime)}
            onClose={handleClose}
          />
        )}
      </Box>
    </AppModal>
  )
}
